import { useEffect } from "react";
import { Alert, App, Button, Card, Form, InputNumber, Select, Space } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ADMIN_ROLES, DEFAULT_APP_CONFIG } from "@glow-price/shared";
import type { AppConfig } from "@glow-price/shared";

import { appConfigApi, channelsApi } from "../api/resources";
import { useCurrentAdmin } from "../auth/useCurrentAdmin";
import { PageHeader } from "../components";

/**
 * 系统配置页。
 *
 * `app_config`（首页置顶渠道 / 降价徽标阈值 / 含税税率）的带校验友好表单。
 * 其它 site_settings（首页 banner/精选、法务文档）各有专属页面编辑，这里不再
 * 暴露裸 JSON。
 */
export function SystemSettingsPage() {
  const me = useCurrentAdmin();
  const allowed =
    !!me && (me.role === ADMIN_ROLES.SUPERADMIN || me.role === ADMIN_ROLES.EDITOR);

  if (!allowed) {
    return (
      <Alert
        type="error"
        showIcon
        message="无权访问"
        description="系统配置仅对超级管理员（superadmin）与编辑（editor）开放。"
      />
    );
  }

  return <SystemSettingsContent />;
}

function SystemSettingsContent() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<AppConfig>();

  const configQuery = useQuery({
    queryKey: ["app-config"],
    queryFn: () => appConfigApi.get(),
  });

  const channelsQuery = useQuery({
    queryKey: ["channels", "for-config"],
    queryFn: () => channelsApi.list({ pageSize: 100 }),
  });

  useEffect(() => {
    if (configQuery.data) form.setFieldsValue(configQuery.data);
  }, [configQuery.data, form]);

  const updateMutation = useMutation({
    mutationFn: (values: AppConfig) => appConfigApi.update(values),
    onSuccess: () => {
      message.success("已保存，前台 60s 内生效");
      qc.invalidateQueries({ queryKey: ["app-config"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const channelOptions = (channelsQuery.data?.items ?? []).map((c) => ({
    value: c.code,
    label: `${c.name}（${c.code}）`,
  }));

  return (
    <div>
      <PageHeader
        title="系统配置"
        description="这些配置原先硬编码在 H5/小程序，现在可在此调整，前台 60s 内生效。"
      />

      <Card loading={configQuery.isLoading} style={{ maxWidth: 720 }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={DEFAULT_APP_CONFIG}
          onFinish={(values) => updateMutation.mutate(values)}
        >
          <Form.Item
            name="pinnedChannelCodes"
            label="首页置顶渠道"
            extra="首页渠道横条置顶展示的渠道，按顺序，H5 与小程序共用"
          >
            <Select
              mode="multiple"
              allowClear
              options={channelOptions}
              loading={channelsQuery.isLoading}
              placeholder="选择并排序置顶渠道"
            />
          </Form.Item>

          <Form.Item
            name="priceDropBadgeThreshold"
            label="降价徽标阈值"
            rules={[{ required: true, message: "请输入阈值" }]}
            extra="降幅达到该百分比才在商品卡显示『降价』徽标"
          >
            <InputNumber min={0} max={100} addonAfter="%" style={{ width: 160 }} />
          </Form.Item>

          <Form.Item
            name="importTaxRatePercent"
            label="含税税率"
            rules={[{ required: true, message: "请输入税率" }]}
            extra="『含税到手』计算使用的行邮税率"
          >
            <InputNumber min={0} max={100} addonAfter="%" style={{ width: 160 }} />
          </Form.Item>

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateMutation.isPending}
            >
              保存
            </Button>
            <Button onClick={() => form.setFieldsValue(DEFAULT_APP_CONFIG)}>
              重置为默认
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}
